import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom'; 
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import API_BASE_URL from '../apiConfig'; // <--- IMPORT
import '../App.css'; 

// --- DYNAMIC URL ---
const API_URL = `${API_BASE_URL}/assessment-result`;

function SuccessPage() {
  const { linkToken } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate(); 
  
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Email State
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');

  // Fetch data on load
  useEffect(() => {
    const fetchResult = async () => {
      if (!token) return;
      try {
        const headers = { 'Authorization': `Bearer ${token}` };
        const response = await axios.get(`${API_URL}/${linkToken}`, { headers });
        setAssessment(response.data);
        setRecipientEmail(response.data.candidate_email || ''); 
      } catch (err) {
        console.error("Error fetching result:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [linkToken, token]);

  // --- HANDLERS ---
  const handleSaveEmail = async () => {
    if (!recipientEmail) return;
    setIsSavingEmail(true);
    try {
        // --- DYNAMIC URL ---
        await axios.put(
            `${API_BASE_URL}/assessment/${assessment.id}/update-email`,
            { candidate_email: recipientEmail },
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        alert("Email updated!");
    } catch (err) {
        alert("Failed to save email.");
    } finally {
        setIsSavingEmail(false);
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) { alert("Enter email."); return; }
    setEmailLoading(true);
    try {
        const payload = {
            candidate_email: recipientEmail,
            job_title: assessment.job_title,
            link: `${window.location.origin}/take/${linkToken}`
        };
        // --- DYNAMIC URL ---
        await axios.post(`${API_BASE_URL}/send-invite`, payload, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 5000);
    } catch (err) {
        alert("Failed to send.");
    } finally {
        setEmailLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/take/${linkToken}`);
    setCopySuccess('Copied!');
    setTimeout(() => setCopySuccess(''), 2000);
  };

  // --- RENDER ---
  if (loading) return <div className="App"><header className="App-header"><h2>Loading...</h2></header></div>;
  if (!assessment) return <div className="App"><header className="App-header"><h2>Not Found</h2></header></div>;

  const candidateLink = `${window.location.origin}/take/${linkToken}`;

  return (
    <div className="App">
      <Navbar />
      <header className="App-header" style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <div className="form-container" style={{maxWidth: '800px', width: '95%'}}>
          
          <div style={{textAlign: 'center', marginBottom: '30px'}}>
            <div style={{fontSize: '4rem', marginBottom: '10px'}}>🎉</div>
            <h2 style={{color: '#2e7d32', marginTop: 0}}>Assessment Created!</h2>
            <p style={{color: '#666', fontSize: '1.1rem'}}>Your custom assessment is ready to send.</p>
          </div>

          {/* --- ACTION CARD --- */}
          <div style={{backgroundColor: '#f9f9f9', padding: '25px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '30px'}}>
            
            {/* EMAIL INPUT */}
            <div style={{marginBottom: '20px'}}>
                <label style={{fontWeight: 'bold', display: 'block', marginBottom: '5px'}}>Send Invite To:</label>
                <div style={{display: 'flex', gap: '10px'}}>
                    <input 
                        type="email" 
                        value={recipientEmail} 
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        style={{flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '1rem'}}
                    />
                    <button onClick={handleSaveEmail} disabled={isSavingEmail} className="secondary-button" style={{padding: '10px 20px'}}>
                        {isSavingEmail ? 'Saving...' : 'Save'}
                    </button>
                </div>
                
                {/* --- NEW TIP: SAVE REMINDER --- */}
                <p style={{fontSize: '0.8rem', color: '#666', marginTop: '8px', marginBottom: '8px', fontStyle: 'italic'}}>
                    ⚠️ <strong>Note:</strong> If you correct the email above, please click <strong>'Save'</strong> to update the record before clicking 'Send Invite'.
                </p>
                {/* ----------------------------- */}

                {/* EXISTING TIP: SPAM REMINDER */}
                <p style={{fontSize: '0.8rem', color: '#888', marginTop: '0px'}}>
                    <strong>Tip:</strong> Automated invites can sometimes land in Spam. Please remind your candidate to check their junk folder if they don't see it immediately.
                </p>
            </div>

            {/* LINK & SEND BUTTONS */}
            <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                <input type="text" readOnly value={candidateLink} style={{flex: 2, padding: '10px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#eee'}} />
                
                <button onClick={handleCopyLink} className="secondary-button" style={{flex: 1}}>
                   {copySuccess || 'Copy Link'}
                </button>
                
                <button onClick={handleSendEmail} className="generate-button" disabled={emailLoading || emailSent} style={{flex: 1}}>
                    {emailLoading ? 'Sending...' : emailSent ? '✅ Sent!' : '📧 Send Invite'}
                </button>
            </div>            
          
          </div>

          {/* --- NEXT STEPS --- */}
          <div style={{display: 'flex', gap: '20px', justifyContent: 'center', width: '100%'}}>
              
              {/* Secondary: Create New */}
              <Link to="/app" className="secondary-button" style={{textDecoration: 'none', flex: 1, maxWidth: '250px', textAlign: 'center'}}>
                Create New Assessment
              </Link>
              
              {/* Primary: View Assessment */}
              <Link to={`/assessment/${linkToken}`} className="generate-button" style={{textDecoration: 'none', flex: 1, maxWidth: '250px', textAlign: 'center'}}>
                View Assessment
              </Link>
          </div>

        </div>
      </header>
      <Footer />
    </div>
  );
}

export default SuccessPage;